import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom-v5-compat';
import { Button, Spinner } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { SlackIcon } from 'src/components/icons/SlackIcon';
import { VaultLogoWithBackground } from 'src/components/icons/VaultLogoWithBackground';
import { InfoIconPopup } from 'src/components/molecules';
import {
  useConnectSlackAccountMutation,
  useGetOrganisationFeaturesQuery,
} from 'src/graphql';
import { getCurrentUser } from 'src/reducers/Reporting.slice';
import * as Types from 'src/types';
import { FeatureFlag } from 'src/types';
import { trackSlackAccountConnectionStepCompleted } from 'src/utils/analytics/track/reporting';
import { default as i18next } from '../../../../i18n';
import { ReportingConnectResult } from '../ReportingConnectResult';
import {
  InfoIconContainer,
  ReportingConnectButtonContainer,
  ReportingConnectContainer,
  ReportingConnectDisclaimer,
  ReportingConnectHeading,
  ReportingConnectListContainer,
  ReportingConnectListHeading,
  ReportingConnectListItem,
  ReportingConnectLogo,
  ReportingConnectPage,
} from './ReportingConnect.styles';

type Permission = {
  heading: string;
  extraInfo: string;
};

export const ReportingConnect = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { state = {} } = useLocation();

  const isPostLoginRedirect = state?.isPostLoginRedirect || false;

  const code = searchParams.get('code');

  const currentUser = useSelector(getCurrentUser);

  const organisationName = currentUser.organisation?.name || 'organisation';
  const [loading, setLoading] = useState<boolean>(false);
  const [attemptedConnect, setAttemptedConnect] = useState<boolean>(false);
  const [connectSuccessful, setConnectSuccessful] = useState<boolean>(false);

  const { data } = useGetOrganisationFeaturesQuery();
  const isSegmentEnabled = isRequiredFeatureEnabled(
    (data?.getOrganisationFeatures as Types.Feature[]) || [],
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const permissions: Permission[] = [
    {
      heading: t('reporting.ReportingConnect.permissionIdentity.heading'),
      extraInfo: t('reporting.ReportingConnect.permissionIdentity.extraInfo'),
    },
    {
      heading: t('reporting.ReportingConnect.permissionSubmitReport.heading'),
      extraInfo: t(
        'reporting.ReportingConnect.permissionSubmitReport.extraInfo',
      ),
    },
    {
      heading: t('reporting.ReportingConnect.permissionNotifications.heading'),
      extraInfo: t(
        'reporting.ReportingConnect.permissionNotifications.extraInfo',
      ),
    },
    {
      heading: t('reporting.ReportingConnect.permissionOverview.heading'),
      extraInfo: t('reporting.ReportingConnect.permissionOverview.extraInfo'),
    },
  ];

  const connectAccount = useConnectSlackAccountMutation({
    onSuccess: (payload) => {
      setAttemptedConnect(true);
      if (payload.connectSlackAccount === 'true') {
        setConnectSuccessful(true);
        if (isSegmentEnabled) {
          trackSlackAccountConnectionStepCompleted({
            organisation_id: currentUser.organisation?.id ?? '',
            user_id: currentUser.user?.userHash || '',
            step_description: 'connect_account_success',
            first_time_connecting: Boolean(
              !currentUser.user?.hasPreviouslyConnectedSlackAccount,
            ),
          });
        }
      }
    },
    onError: () => {
      setAttemptedConnect(true);
      setConnectSuccessful(false);
      if (isSegmentEnabled) {
        trackSlackAccountConnectionStepCompleted({
          organisation_id: currentUser.organisation?.id ?? '',
          user_id: currentUser.user?.userHash || '',
          step_description: 'connect_account_failure',
          first_time_connecting: Boolean(
            !currentUser.user?.hasPreviouslyConnectedSlackAccount,
          ),
        });
      }
    },
  });

  const attemptConnect = () => {
    setLoading(true);

    if (isSegmentEnabled) {
      trackSlackAccountConnectionStepCompleted({
        organisation_id: currentUser.organisation?.id ?? '',
        user_id: currentUser.user?.userHash || '',
        step_description: 'accept_terms',
        first_time_connecting: Boolean(
          !currentUser.user?.hasPreviouslyConnectedSlackAccount,
        ),
      });
    }

    if (code) {
      connectAccount.mutate({
        authCode: code,
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (isPostLoginRedirect && isSegmentEnabled) {
      trackSlackAccountConnectionStepCompleted({
        organisation_id: currentUser.organisation?.id ?? '',
        user_id: currentUser.user?.userHash || '',
        step_description: 'login',
        first_time_connecting: Boolean(
          !currentUser.user?.hasPreviouslyConnectedSlackAccount,
        ),
      });
    }
    // Ignoring as we want this to run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSegmentEnabled]);

  return (
    <ReportingConnectPage>
      {!loading ? (
        !attemptedConnect ? (
          <ReportingConnectContainer>
            <ReportingConnectLogo>
              <VaultLogoWithBackground size={70} />
              <StyledIcon className="uil uil-exchange" size="70px" />
              <SlackIcon size={70} />
            </ReportingConnectLogo>
            <ReportingConnectHeading>
              {t('reporting.ReportingConnect.heading', {
                organisationName,
              })}
            </ReportingConnectHeading>
            <ReportingConnectListHeading>
              {t('reporting.ReportingConnect.listHeading')}
            </ReportingConnectListHeading>
            <ReportingConnectListContainer>
              {permissions.map((permission, i) => (
                <ReportingConnectListItem key={i}>
                  {permission.heading}
                  <InfoIconContainer>
                    <InfoIconPopup text={permission.extraInfo} />
                  </InfoIconContainer>
                </ReportingConnectListItem>
              ))}
            </ReportingConnectListContainer>
            <ReportingConnectButtonContainer>
              <Button variant="secondary">
                {t('reporting.ReportingConnect.cancelButton')}
              </Button>
              <Button onClick={() => attemptConnect()} disabled={!code}>
                {t('reporting.ReportingConnect.connectButton')}
              </Button>
            </ReportingConnectButtonContainer>
            <ReportingConnectDisclaimer>
              <Trans
                i18n={i18next}
                shouldUnescape
                i18nKey="reporting.ReportingConnect.disclaimer"
                components={{
                  bold: <strong />,
                }}
              />
            </ReportingConnectDisclaimer>
          </ReportingConnectContainer>
        ) : (
          <ReportingConnectResult connectSuccessful={connectSuccessful} />
        )
      ) : (
        <Spinner variant="secondary" size="xlarge" />
      )}
    </ReportingConnectPage>
  );
};
